import React, { useEffect, useState } from "react";
import axios from "../../api/axiosConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSync } from "react-icons/fa";
import TablaPedidos from "./components/TablaPedidos";

function PedidoList({ onSelectPedido }) {
  const navigate = useNavigate();

  /* ===============================
     USUARIO Y ROL
  =============================== */
  const getUsuario = () => {
    try {
      return JSON.parse(localStorage.getItem("usuario")) || {};
    } catch {
      return {};
    }
  };

  const usuario = getUsuario();

  const rol = String(
    usuario?.rol || localStorage.getItem("rol") || "MESERO"
  ).toUpperCase();

  const nombreUsuario = usuario?.nombre || "";

  const puedeGestionar = rol === "ADMIN" || rol === "CAJERO";

  /* ===============================
     ESTADOS
  =============================== */
  const [pedidos, setPedidos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filtroUsuario, setFiltroUsuario] = useState(
    rol === "MESERO" ? nombreUsuario : ""
  );
  const [filtroEstado, setFiltroEstado] = useState("TODOS");
  const [filtroMesa, setFiltroMesa] = useState("");

  // Cancelación
  const [mostrarCancelar, setMostrarCancelar] = useState(false);
  const [pedidoCancelar, setPedidoCancelar] = useState(null);
  const [motivoCancelacion, setMotivoCancelacion] = useState("");

  /* ===============================
     EFECTOS
  =============================== */
  useEffect(() => {
    const controller = new AbortController();
    obtenerPedidos(controller.signal);
    obtenerUsuarios(controller.signal);

    return () => controller.abort();
  }, []);

  /* ===============================
     API
  =============================== */
  const obtenerPedidos = async (signal) => {
    setLoading(true);
    try {
      const res = await axios.get("/pedidos/abiertos", {
        ...(signal && { signal }),
      });
      setPedidos(res.data || []);
    } catch (e) {
      if (e.name !== "CanceledError") {
        toast.error("❌ Error al obtener pedidos");
      }
    } finally {
      setLoading(false);
    }
  };

  const obtenerUsuarios = async (signal) => {
    try {
      const res = await axios.get("/usuarios", {
        ...(signal && { signal }),
      });
      setUsuarios(res.data || []);
    } catch (e) {
      if (e.name !== "CanceledError") {
        console.error("❌ Error cargando usuarios", e);
      }
    }
  };


  const cambiarEstado = async (pedido) => {
    try {
      await axios.put(`/pedidos/${pedido.id}`, {
        ...pedido,
        entregado: !pedido.entregado,
      });
      toast.info("Estado del pedido actualizado");
      obtenerPedidos();
    } catch {
      toast.error("Error al cambiar estado");
    }
  };

  const openModalCancelar = (pedido) => {
    setPedidoCancelar(pedido);
    setMotivoCancelacion("");
    setMostrarCancelar(true);
  };

  const cancelarPedido = async () => {
    if (!motivoCancelacion.trim()) {
      toast.error("Debes ingresar un motivo");
      return;
    }

    try {
      await axios.put(`/pedidos/${pedidoCancelar.id}`, {
        ...pedidoCancelar,
        cancelado: true,
        objecion: motivoCancelacion,
      });

      toast.success("Pedido anulado");
      setMostrarCancelar(false);
      obtenerPedidos();
    } catch {
      toast.error("Error al anular pedido");
    }
  };

  /* ===============================
     UTILIDADES
  =============================== */
  const calcularTotalPedido = (pedido) =>
    (pedido?.detalles || []).reduce(
      (acc, d) => acc + (parseFloat(d.subtotal) || 0),
      0
    );

  /* ===============================
     FILTROS
  =============================== */
  const pedidosFiltrados = pedidos.filter((p) => {
    if (p.cancelado) return false;

    const estadoOk =
      filtroEstado === "TODOS"
        ? true
        : filtroEstado === "ENTREGADO"
        ? p.entregado
        : !p.entregado;

    const mesaOk = filtroMesa
      ? p.mesa?.numero?.toString().includes(filtroMesa)
      : true;

    const usuarioOk = filtroUsuario
      ? (p.mesero?.nombre || "")
          .toLowerCase()
          .includes(filtroUsuario.toLowerCase())
      : true;

    return estadoOk && mesaOk && usuarioOk;
  });

  /* ===============================
     RENDER
  =============================== */
  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white/95 dark:bg-slate-900/90 rounded-2xl shadow-xl 
                      border border-slate-200 dark:border-slate-800 p-6 mt-3">

        {/* HEADER */}
        <div className="flex flex-col sm:flex-row justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-amber-500">
              Gestión de Pedidos
            </h2>
            <p className="text-xs text-slate-500">
              Administra los pedidos abiertos del restaurante.
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => obtenerPedidos()}
              className="px-3 py-1.5 rounded-lg border text-sm flex items-center gap-1"
            >
              <FaSync /> Actualizar
            </button>

            <button
              onClick={() => navigate("/pedidos/nuevo")}
              className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-sm flex items-center gap-1"
            >
              <FaPlus /> Nuevo Pedido
            </button>
          </div>
        </div>

        {/* FILTROS */}
        <div className="mb-5 p-4 rounded-xl border bg-slate-50 dark:bg-slate-900/60">
          <div className="flex flex-col md:flex-row gap-3">
            <input
              placeholder="Buscar mesa..."
              value={filtroMesa}
              onChange={(e) => setFiltroMesa(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border"
            />

            <select
              value={filtroUsuario}
              onChange={(e) => setFiltroUsuario(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border"
            >
              <option value="">Todos los usuarios</option>
              {usuarios.map((u) => (
                <option key={u.id} value={u.nombre}>
                  {u.nombre}
                </option>
              ))}
            </select>

            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border"
            >
              <option value="TODOS">Todos</option>
              <option value="PENDIENTE">Pendientes</option>
              <option value="ENTREGADO">Entregados</option>
            </select>
          </div>
        </div>

        {/* TABLA */}
        <TablaPedidos
          pedidos={pedidosFiltrados}
          loading={loading}
          puedeGestionar={puedeGestionar}
          onSelectPedido={onSelectPedido}
          onCambiarEstado={cambiarEstado}
          onCancelarPedido={openModalCancelar}
          calcularTotalPedido={calcularTotalPedido}
        />
      </div>

      {/* MODAL CANCELAR */}
      {mostrarCancelar && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 w-[95%] max-w-md">
            <h3 className="font-semibold mb-2">
              Anular pedido #{pedidoCancelar?.id}
            </h3>

            <textarea
              value={motivoCancelacion}
              onChange={(e) => setMotivoCancelacion(e.target.value)}
              className="w-full h-24 border rounded-lg p-2"
              placeholder="Motivo de cancelación..."
            />

            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setMostrarCancelar(false)}>
                Cancelar
              </button>
              <button
                onClick={cancelarPedido}
                className="bg-rose-600 text-white px-3 py-1 rounded-lg"
              >
                Anular
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PedidoList;
